// Country feature splitting: separates MultiPolygon countries into individual
// island features, normalizes country names, and classifies parts as
// mainland vs island vs exclave. Pure functions — no external dependencies.

import { unwrapAntimeridian } from './antimeridian';
import { computeBbox } from '@/lib/worldMap/mapUtils';

// The Natural Earth 50m dataset already uses clean English names for most
// countries. These are the few that benefit from normalization.
const NAME_MAP = {
  'Czechia': 'Czech Republic',
  'Cabo Verde': 'Cape Verde',
  'eSwatini': 'Eswatini',
};

// Territories that are not countries (uninhabited / continental landmasses).
const EXCLUDED = new Set(['French Southern and Antarctic Lands']);

function normalizeCountryName(raw) {
  return NAME_MAP[raw] || raw;
}

// Bounding-box center of a polygon (uses its outer ring).
function polygonCenter(poly) {
  const { minLat, maxLat, minLng, maxLng } = computeBbox(poly[0]);
  return [(minLat + maxLat) / 2, (minLng + maxLng) / 2];
}

// Bounding-box area of a ring (deg²). Returns -1 if it wraps the antimeridian.
function bboxArea(ring) {
  const { minLat, maxLat, minLng, maxLng } = computeBbox(ring);
  const span = maxLng - minLng;
  if (span > 180) return -1;
  return (maxLat - minLat) * span;
}

// Known islands to split out of their parent with a friendly code/name (kept for
// back-compat with existing visited records that used these codes).
const ISLANDS = [
  { parent: 'Italy', id: 'SAR', name: 'Sardinia', lat: [38.8, 41.3], lng: [8, 10] },
  { parent: 'Italy', id: 'SIC', name: 'Sicily', lat: [36.6, 38.3], lng: [12, 15.6] },
  { parent: 'France', id: 'COR', name: 'Corsica', lat: [41.3, 43.5], lng: [8.5, 9.7] },
  { parent: 'Greece', id: 'CRE', name: 'Crete', lat: [34.8, 35.7], lng: [23.5, 26.5] },
  { parent: 'Canada', id: 'NFD', name: 'Newfoundland', lat: [46.6, 51.7], lng: [-59.5, -52.2] },
  { parent: 'Australia', id: 'TAS', name: 'Tasmania', lat: [-43.7, -40.3], lng: [144.6, 148.5] },
  { parent: 'China', id: 'HAI', name: 'Hainan', lat: [18.0, 20.2], lng: [108.6, 111.5] },
  { parent: 'Russia', id: 'SAK', name: 'Sakhalin', lat: [46, 54], lng: [141, 144] },
  { parent: 'Portugal', id: 'MDR', name: 'Madeira', lat: [32.4, 32.9], lng: [-17.2, -16.1] },
];

// Region classification for the custom island codes (not in the standard dataset).
const REGION_OVERRIDES = {
  SAR: { region: 'Europe', subRegion: 'Southern Europe' },
  SIC: { region: 'Europe', subRegion: 'Southern Europe' },
  COR: { region: 'Europe', subRegion: 'Western Europe' },
  CRE: { region: 'Europe', subRegion: 'Southern Europe' },
  NFD: { region: 'America', subRegion: 'Northern America' },
  TAS: { region: 'Oceania', subRegion: 'Australia and New Zealand' },
  HAI: { region: 'Asia', subRegion: 'Eastern Asia' },
  SAK: { region: 'Asia', subRegion: 'Eastern Asia' },
  MDR: { region: 'Europe', subRegion: 'Southern Europe' },
};

// Split every MultiPolygon country into individual Polygon features so each
// detached landmass is independently selectable. The largest polygon keeps
// the country's ISO code for back-compat; known islands keep their custom
// codes and all other detached polygons get `<code>~<index>`.
function splitParts(features) {
  const out = [];
  for (const f of features) {
    const code = f.id;
    const name = f.properties.name;
    if (!f.geometry) continue;
    if (f.geometry.type === 'Polygon') {
      const [lat, lng] = polygonCenter(f.geometry.coordinates);
      out.push({ ...f, properties: { ...f.properties, partType: 'country', country: code, code, individual: false, lat, lng } });
      continue;
    }
    if (f.geometry.type !== 'MultiPolygon') {
      out.push(f);
      continue;
    }
    const polys = f.geometry.coordinates;
    const targets = ISLANDS.filter((t) => t.parent === name);

    // mainland = largest non-antimeridian-wrapping polygon
    let mainlandIdx = 0;
    let bestArea = -1;
    polys.forEach((p, i) => {
      const a = bboxArea(p[0]);
      if (a > bestArea) {
        bestArea = a;
        mainlandIdx = i;
      }
    });

    const knownIslandParts = new Map();
    for (const target of targets) {
      let bestMatch = null;
      polys.forEach((p, i) => {
        if (i === mainlandIdx) return;
        const [lat, lng] = polygonCenter(p);
        if (lat <= target.lat[0] || lat >= target.lat[1] || lng <= target.lng[0] || lng >= target.lng[1]) return;
        const area = bboxArea(p[0]);
        if (!bestMatch || area > bestMatch.area) bestMatch = { index: i, area };
      });
      if (bestMatch) knownIslandParts.set(bestMatch.index, target);
    }

    const parts = [];
    polys.forEach((p, i) => {
      if (i === mainlandIdx) {
        const [lat, lng] = polygonCenter(p);
        parts.push({ code, name, ring: p, partType: 'country', individual: false, lat, lng });
        return;
      }
      const [lat, lng] = polygonCenter(p);
      const match = knownIslandParts.get(i);
      if (match) {
        parts.push({ code: match.id, name: match.name, ring: p, partType: 'island', individual: true, lat, lng });
        return;
      }
      parts.push({ code: `${code}~${i}`, name, ring: p, partType: 'island', individual: true, lat, lng });
    });

    parts.forEach((part) => {
      out.push({
        type: 'Feature',
        id: part.code,
        properties: { name: part.name, partType: part.partType, country: code, code: part.code, individual: !!part.individual, ...(part.lat != null ? { lat: part.lat, lng: part.lng } : {}) },
        geometry: { type: 'Polygon', coordinates: part.ring },
      });
    });
  }
  return out;
}

// Cache the expensive split/unwrap result by input reference — loadCountryFeatures
// returns the same cached array, so cleanFeatures is computed once across the
// map, bucket list, and statistics pages instead of three times.
let _cleanedKey = null;
let _cleanedVal = null;
export function cleanFeatures(features) {
  if (_cleanedKey === features) return _cleanedVal;
  // Already processed (from cleaned cache) — return as-is, skip splitParts.
  if (features.length > 0 && features[0]?.properties?.partType) {
    _cleanedKey = features;
    _cleanedVal = features;
    return features;
  }
  const cleaned = features
    .filter((f) => !EXCLUDED.has(f.properties.name))
    .map((f) => ({
      ...f,
      properties: { ...f.properties, name: normalizeCountryName(f.properties.name) },
    }));
  const result = splitParts(unwrapAntimeridian(cleaned));
  // Do not retain the raw GeoJSON as a memoization key. The processed result
  // is reused by callers, while keeping the raw array here doubles map memory
  // for the lifetime of the WebView session.
  _cleanedKey = null;
  _cleanedVal = result;
  return result;
}

export { REGION_OVERRIDES };